import React, { useState, useEffect, useRef, useCallback } from 'react';
import { forumApi } from '../../api/forum';
import '../../css/components/TagInput.css';

/**
 * Поле ввода тегов с автодополнением
 *
 * @param {Object} props
 * @param {string[]} props.value — массив выбранных тегов (строки)
 * @param {Function} props.onChange — callback(tags: string[])
 * @param {boolean} [props.disabled=false]
 * @param {string} [props.placeholder]
 */
export default function TagInput({ value = [], onChange, disabled = false, placeholder = 'Начните вводить тег...' }) {
    const [input, setInput] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [allTags, setAllTags] = useState([]);
    const [tagsLoaded, setTagsLoaded] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [activeSuggestion, setActiveSuggestion] = useState(-1);
    const containerRef = useRef(null);
    const inputRef = useRef(null);

    // Загрузка всех тегов один раз
    useEffect(() => {
        let cancelled = false;
        const fetchTags = async () => {
            try {
                const data = await forumApi.listTags();
                if (!cancelled) {
                    setAllTags(data || []);
                    setTagsLoaded(true);
                }
            } catch (err) {
                console.warn('[TagInput] Failed to fetch tags:', err.message);
            }
        };
        fetchTags();
        return () => { cancelled = true; };
    }, []);

    // Фильтрация подсказок при вводе
    const updateSuggestions = useCallback((query) => {
        if (!query.trim()) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }

        const lowerQuery = query.trim().toLowerCase();
        const existing = allTags
            .map(t => typeof t === 'string' ? t : (t.name || t.slug || ''))
            .filter(name =>
                name.toLowerCase().includes(lowerQuery) &&
                !value.some(v => v.toLowerCase() === name.toLowerCase())
            )
            .slice(0, 6);

        setSuggestions(existing);
        // Показываем дропдаун даже без совпадений — для empty state и опции создания
        setShowSuggestions(true);
        setActiveSuggestion(-1);
    }, [allTags, value]);

    const handleInputChange = (e) => {
        setInput(e.target.value);
        updateSuggestions(e.target.value);
    };

    const addTag = (tag) => {
        const trimmed = tag.trim().replace(/^#/, '');
        if (!trimmed) return;
        // Проверка на дубликат (регистронезависимо)
        if (value.some(v => v.toLowerCase() === trimmed.toLowerCase())) {
            setInput('');
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }
        onChange([...value, trimmed]);
        setInput('');
        setSuggestions([]);
        setShowSuggestions(false);
        inputRef.current?.focus();
    };

    const removeTag = (indexToRemove) => {
        onChange(value.filter((_, i) => i !== indexToRemove));
    };

    const handleKeyDown = (e) => {
        // Enter — добавить тег (из подсказки или из инпута)
        if (e.key === 'Enter') {
            e.preventDefault();
            if (activeSuggestion >= 0 && suggestions[activeSuggestion]) {
                addTag(suggestions[activeSuggestion]);
            } else if (input.trim()) {
                addTag(input);
            }
            return;
        }

        // Стрелка вниз
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (suggestions.length > 0) {
                setActiveSuggestion(prev =>
                    prev < suggestions.length - 1 ? prev + 1 : 0
                );
            }
            return;
        }

        // Стрелка вверх
        if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (suggestions.length > 0) {
                setActiveSuggestion(prev =>
                    prev > 0 ? prev - 1 : suggestions.length - 1
                );
            }
            return;
        }

        // Backspace при пустом инпуте — удалить последний тег
        if (e.key === 'Backspace' && !input && value.length > 0) {
            e.preventDefault();
            removeTag(value.length - 1);
            return;
        }

        // Escape — закрыть подсказки
        if (e.key === 'Escape') {
            setShowSuggestions(false);
            return;
        }

        // Запятая — разделитель, добавляем тег
        if (e.key === ',' && input.trim()) {
            e.preventDefault();
            addTag(input);
        }
    };

    const handleSuggestionClick = (suggestion) => {
        addTag(suggestion);
    };

    const handleBlur = (e) => {
        // Если клик был внутри контейнера — не закрываем
        if (containerRef.current?.contains(e.relatedTarget)) return;
        // Добавляем незавершённый тег при потере фокуса
        if (input.trim()) {
            addTag(input);
        }
        setShowSuggestions(false);
    };

    return (
        <div className="tag-input-container" ref={containerRef}>
            <div className="tag-input-tags">
                {value.map((tag, i) => (
                    <span key={i} className="tag-input-chip">
                        #{tag}
                        {!disabled && (
                            <button
                                type="button"
                                className="tag-input-chip-remove"
                                onClick={() => removeTag(i)}
                                title="Удалить тег"
                            >
                                ×
                            </button>
                        )}
                    </span>
                ))}
                <input
                    ref={inputRef}
                    type="text"
                    className="tag-input-field"
                    value={input}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                    onFocus={() => { if (input.trim()) updateSuggestions(input); }}
                    placeholder={value.length === 0 ? placeholder : ''}
                    disabled={disabled}
                />
            </div>

            {showSuggestions && (
                <div className="tag-input-suggestions">
                    {suggestions.map((suggestion, i) => (
                        <div
                            key={i}
                            className={`tag-input-suggestion ${i === activeSuggestion ? 'tag-input-suggestion--active' : ''}`}
                            onMouseDown={(e) => { e.preventDefault(); handleSuggestionClick(suggestion); }}
                            onMouseEnter={() => setActiveSuggestion(i)}
                        >
                            #{suggestion}
                        </div>
                    ))}
                    {suggestions.length === 0 && tagsLoaded && (
                        <div className="tag-input-suggestion tag-input-suggestion--empty">
                            Нет существующих тегов
                        </div>
                    )}
                    {input.trim() && !allTags.some(t => {
                        const name = typeof t === 'string' ? t : (t.name || '');
                        return name.toLowerCase() === input.trim().toLowerCase();
                    }) && (
                        <div
                            className="tag-input-suggestion tag-input-suggestion--new"
                            onMouseDown={(e) => { e.preventDefault(); addTag(input); }}
                        >
                            + Создать тег «{input.trim()}»
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}